import { READ_ONLY_INPUT_STYLE } from "@leedpulse-monorepo/components";
import { Button, Flex, Form, Input, InputNumber, Popconfirm, Switch } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

interface RecordInputProps {
    namePath: (string | number)[];
    data: Record<string, unknown>;
    isReadOnly: boolean;
    depth?: number;
    onRemove?: (path: (string | number)[]) => void;
    onAdd?: (path: (string | number)[], existingKeys: string[]) => void;
}

const formatLabel = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RecordInput: React.FC<RecordInputProps> = ({ namePath, data, isReadOnly, depth = 0, onRemove, onAdd }) => {
    // Sort alphabetically: Firestore returns object keys in arbitrary order, so without
    // this the field order would shuffle on every refresh.
    const keys = Object.keys(data).sort((a, b) => a.localeCompare(b));

    const fieldLabel = (key: string, path: (string | number)[]): React.ReactNode =>
      !onRemove ? formatLabel(key) : (
        <Flex align="center" gap={8}>
          <span>{formatLabel(key)}</span>
          <Popconfirm
            title="Supprimer ce champ ?"
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
            onConfirm={() => onRemove(path)}
          >
            <DeleteOutlined style={{ color: 'var(--danger-color)' }} />
          </Popconfirm>
        </Flex>
      );

    const addButton = onAdd && !isReadOnly ? (
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => onAdd(namePath, keys)}
        style={{ alignSelf: 'flex-start' }}
      >
        Ajouter
      </Button>
    ) : null;

    return (
      <Flex
        vertical
        gap={12}
        style={{
          width: '100%',
          paddingLeft:  12,
          borderLeft: '2px solid var(--background-color, #f0f0f0)' ,
        }}
      >
        {keys.length === 0 ? (
          <span style={{ color: 'var(--text-color-secondary, rgba(0,0,0,0.45))', fontSize: 12 }}>
            (Objet vide)
          </span>
        ) : (
          <Flex
            vertical
            gap={12}
            style={depth === 0 ? { maxHeight: 360, overflowY: 'auto' } : undefined}
          >
        {keys.map((key) => {
          const val = data[key];
          const leafPath = [...namePath, key];

          if (typeof val === 'boolean') {
            return (
              <Form.Item
                key={key}
                label={fieldLabel(key, leafPath)}
                extra={key}
                name={leafPath}
                valuePropName="checked"
              >
                <Switch disabled={isReadOnly} />
              </Form.Item>
            );
          }

          if (typeof val === 'number') {
            return (
              <Form.Item key={key} label={fieldLabel(key, leafPath)} extra={key} name={leafPath}>
                <InputNumber readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }} />
              </Form.Item>
            );
          }

          if (typeof val === 'string') {
            return (
              <Form.Item key={key} label={fieldLabel(key, leafPath)} extra={key} name={leafPath}>
                <Input readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
              </Form.Item>
            );
          }

          if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
            return (
              <Flex
                key={key}
                vertical
                gap={8}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--background-color)',
                }}
              >
                <Flex align="center" justify="space-between" gap={8}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{formatLabel(key)}</div>
                  {onRemove && (
                    <Popconfirm
                      title="Supprimer ce champ ?"
                      okText="Supprimer"
                      cancelText="Annuler"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onRemove(leafPath)}
                    >
                      <DeleteOutlined style={{ color: 'var(--danger-color)' }} />
                    </Popconfirm>
                  )}
                </Flex>
                <div style={{ fontSize: 11, opacity: 0.65, marginTop: -4 }}>{key}</div>
                <RecordInput
                  namePath={leafPath}
                  data={val as Record<string, unknown>}
                  isReadOnly={isReadOnly}
                  depth={depth + 1}
                  onRemove={onRemove}
                  onAdd={onAdd}
                />
              </Flex>
            );
          }

          if (Array.isArray(val)) {
            return (
              <Form.Item
                key={key}
                label={fieldLabel(key, leafPath)}
                extra={key}
                name={leafPath}
                getValueProps={(v) => ({
                  value: v !== undefined ? JSON.stringify(v, null, 2) : '',
                })}
                getValueFromEvent={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  try {
                    return JSON.parse(e.target.value);
                  } catch {
                    return e.target.value;
                  }
                }}
              >
                <Input.TextArea rows={4} readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
              </Form.Item>
            );
          }

          return (
            <Form.Item key={key} label={fieldLabel(key, leafPath)} extra={key} name={leafPath}>
              <Input
                readOnly={isReadOnly}
                placeholder={val === null || val === undefined ? String(val) : undefined}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              />
            </Form.Item>
          );
        })}
          </Flex>
        )}
        {addButton}
      </Flex>
    );
  };

export default RecordInput;
